import { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
    Box,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Checkbox,
    ListItemText,
    Button,
    OutlinedInput,
    TextField,
    InputAdornment,
    Typography,
} from '@mui/material';
import { Iconify } from 'src/components';
import useDemoMode, { buildPracticeNameMasker } from 'src/hooks/useDemoMode';

export default function PracticeSelector({ practices, selectedPracticeIds, onSelectionChange, rightSlot }) {
    const [searchQuery, setSearchQuery] = useState('');
    const [demoMode] = useDemoMode();
    const maskName = useMemo(() => buildPracticeNameMasker(practices, demoMode), [practices, demoMode]);

    const filteredPractices = useMemo(() => {
        if (!searchQuery) return practices || [];
        if (demoMode) return practices || []; // search disabled in demo mode to prevent name leakage
        const query = searchQuery.toLowerCase();
        return (practices || []).filter((p) => p.name?.toLowerCase().includes(query));
    }, [practices, searchQuery, demoMode]);

    const handleChange = (event) => {
        const {value} = event.target;
        // Handle "Select All" option
        if (value.includes('all')) {
            if (selectedPracticeIds.length === practices?.length) {
                onSelectionChange([]);
            } else {
                onSelectionChange(practices?.map((p) => String(p.id)) || []);
            }
        } else {
            onSelectionChange(typeof value === 'string' ? value.split(',') : value);
        }
    };

    const handleSelectAll = () => {
        if (selectedPracticeIds.length === practices?.length) {
            onSelectionChange([]);
        } else {
            onSelectionChange(practices?.map((p) => String(p.id)) || []);
        }
    };

    const allSelected = selectedPracticeIds.length === practices?.length && practices?.length > 0;

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                bgcolor: '#fff',
                borderRadius: '12px',
                p: 2,
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 40,
                    height: 40,
                    borderRadius: '10px',
                    bgcolor: '#e0f2fe',
                    color: '#0369a1',
                }}
            >
                <Iconify icon="mdi:office-building" width={24} height={24} />
            </Box>

            <FormControl sx={{ minWidth: 280, flex: 1, maxWidth: 400 }} size="small">
                <InputLabel id="practice-selector-label">Select Practices</InputLabel>
                <Select
                    labelId="practice-selector-label"
                    multiple
                    value={selectedPracticeIds}
                    onChange={handleChange}
                    input={<OutlinedInput label="Select Practices" />}
                    renderValue={(selected) => {
                        if (selected.length === 0) return 'No practices selected';
                        if (selected.length === practices?.length) return 'All practices selected';
                        return `${selected.length} practice${selected.length > 1 ? 's' : ''} selected`;
                    }}
                    onClose={() => setSearchQuery('')}
                    MenuProps={{
                        PaperProps: {
                            style: {
                                maxHeight: 350,
                            },
                        },
                        autoFocus: false,
                    }}
                >
                    {/* Search Input */}
                    <Box sx={{ px: 1.5, py: 1, position: 'sticky', top: 0, bgcolor: '#fff', zIndex: 1 }}>
                        <TextField
                            size="small"
                            fullWidth
                            placeholder="Search practices..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            onKeyDown={(e) => e.stopPropagation()}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Iconify icon="eva:search-fill" width={18} sx={{ color: 'text.disabled' }} />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '8px',
                                    fontSize: '13px',
                                },
                            }}
                        />
                    </Box>

                    {/* Select All Option */}
                    {!searchQuery && (
                        <MenuItem value="all" sx={{ borderBottom: '1px solid #e2e8f0' }}>
                            <Checkbox
                                checked={allSelected}
                                indeterminate={selectedPracticeIds.length > 0 && !allSelected}
                            />
                            <ListItemText
                                primary="Select All"
                                primaryTypographyProps={{ fontWeight: 600 }}
                            />
                        </MenuItem>
                    )}

                    {/* Practice Options */}
                    {filteredPractices.map((practice) => (
                        <MenuItem key={practice.id} value={String(practice.id)}>
                            <Checkbox checked={selectedPracticeIds.includes(String(practice.id))} />
                            <ListItemText primary={maskName(practice)} />
                        </MenuItem>
                    ))}

                    {/* No results */}
                    {filteredPractices.length === 0 && searchQuery && (
                        <Box sx={{ px: 2, py: 1.5, textAlign: 'center' }}>
                            <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>
                                No practices found
                            </Typography>
                        </Box>
                    )}
                </Select>
            </FormControl>

            <Button
                variant={allSelected ? 'outlined' : 'contained'}
                size="small"
                onClick={handleSelectAll}
                sx={{
                    textTransform: 'none',
                    borderRadius: '8px',
                    fontWeight: 500,
                    minWidth: 100,
                }}
            >
                {allSelected ? 'Deselect All' : 'Select All'}
            </Button>

            {rightSlot && (
                <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
                    {rightSlot}
                </Box>
            )}
        </Box>
    );
}

PracticeSelector.propTypes = {
    practices: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            name: PropTypes.string,
        })
    ),
    selectedPracticeIds: PropTypes.arrayOf(PropTypes.string),
    onSelectionChange: PropTypes.func.isRequired,
    rightSlot: PropTypes.node,
};

PracticeSelector.defaultProps = {
    practices: [],
    selectedPracticeIds: [],
    rightSlot: null,
};
